import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Loader2, Search } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, Field } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { OknoLimituModal } from '@/components/OknoLimituModal';
import { opcjeZakresuLimitu } from '@/lib/rationing-api';
import type { Okno } from '@/lib/permissions-api';
import { cn } from '@/lib/utils';

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  NOWY LIMIT IMIENNY — najpierw ZAKRES, potem wielkość i okno.
 *
 *  ⚠️⚠️ ZAMÓWIONE 07.09.2026: „musi wszystko być możliwe do ustawienia […] wszystko musi być
 *  w miarę w jednym miejscu". Limit imienny nadaje się więc z karty pracownika, bo tam już stoją
 *  pozostałe jego limity — a w Uprawnieniach nie ma do czego go doczepić, gdy dostęp idzie
 *  przez grupę.
 *
 *  ⚠️⚠️ DWA KROKI, NIE JEDEN FORMULARZ. Zakres jest KLUCZEM NATURALNYM reguły w SPS (razem
 *  z celem), więc jego wybór to nie „pole" — po zapisie nie da się go zmienić, tylko usunąć
 *  regułę i utworzyć drugą. Rozdzielenie kroków mówi to układem: najpierw rozstrzygasz, CZEGO
 *  limit dotyczy, potem ILE.
 *
 *  ⚠️⚠️ PODGRUPY NIE MA NA LIŚCIE i to jest zgodne z SPS, nie ograniczenie portalu.
 *  `Rationing.ITEMTYPE` zna wyłącznie towar (7) i grupę GŁÓWNĄ (8). Limit „na podgrupę" nadaje
 *  się przez jej grupę nadrzędną — z ekranu Uprawnień, przy wpisie dostępu, gdzie widać, że
 *  obejmie całą grupę.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */
export function OknoLimituImiennego({
  groupId,
  imie,
  onClose,
  onZapisz,
}: {
  groupId: string;
  imie: string;
  onClose: () => void;
  onZapisz: (zakres: { itemId?: string; itemGroupId?: string }, okno: Okno) => Promise<void>;
}) {
  const [szukaj, setSzukaj] = useState('');
  const [wybor, setWybor] = useState<{ itemId?: string; itemGroupId?: string; nazwa: string } | null>(null);

  const opcje = useQuery({
    queryKey: ['opcje-limitu', groupId, szukaj],
    queryFn: () => opcjeZakresuLimitu(groupId, szukaj),
  });

  /** ⚠️ Grupy GŁÓWNE — podgrupy odsiane, bo `Rationing` ich nie wyraża (patrz nagłówek). */
  const grupy = (opcje.data?.itemGroups ?? []).filter((g) => !g.isSubGroup);
  const towary = opcje.data?.items ?? [];
  const fraza = szukaj.trim().toLowerCase();
  const grupyWidoczne =
    fraza === ''
      ? grupy
      : grupy.filter((g) => g.name.toLowerCase().includes(fraza) || g.code.toLowerCase().includes(fraza));

  if (wybor !== null) {
    return (
      <OknoLimituModal
        tytul="Nowy limit imienny"
        opis={`${wybor.nazwa} dla „${imie}" — reguła obowiązuje TYLKO tę osobę`}
        poczatkowe={null}
        onClose={onClose}
        onZapisz={async (o) => {
          await onZapisz(
            wybor.itemId === undefined ? { itemGroupId: wybor.itemGroupId } : { itemId: wybor.itemId },
            o,
          );
        }}
      />
    );
  }

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        title="Nowy limit imienny"
        description={`Czego ma dotyczyć limit dla „${imie}"`}
      >
        <Field label="Szukaj" htmlFor="szukaj-zakresu">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              id="szukaj-zakresu"
              autoFocus
              className="pl-8"
              placeholder="nazwa grupy albo towaru"
              value={szukaj}
              onChange={(e) => setSzukaj(e.target.value)}
            />
          </div>
        </Field>

        {opcje.isPending && (
          <p className="flex items-center gap-2 py-2 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" /> Ładowanie…
          </p>
        )}
        {opcje.isError && <p className="text-sm text-[var(--destructive)]">{String(opcje.error)}</p>}

        {opcje.data !== undefined && (
          <div className="max-h-[24rem] space-y-3 overflow-y-auto">
            {/*
              ⚠️ GRUPY PRZED TOWARAMI, bo limit na grupie jest tym, czego klienci używają
              (na zmierzonej wyspie wszystkie 125 reguł to reguły na grupach towarowych).
              Limit na jednym towarze jest wyjątkiem i stoi niżej.
            */}
            <div>
              <p className="mb-1 text-xs font-medium text-muted-foreground">Grupy produktów</p>
              {grupyWidoczne.length === 0 ? (
                <p className="text-xs text-muted-foreground">Nic nie pasuje</p>
              ) : (
                <ul className="divide-y divide-border rounded-md border border-border">
                  {grupyWidoczne.slice(0, 40).map((g) => (
                    <li key={g.id}>
                      <button
                        type="button"
                        className="flex w-full items-baseline justify-between gap-2 px-2.5 py-1.5 text-left text-sm hover:bg-accent"
                        onClick={() => setWybor({ itemGroupId: g.id, nazwa: `grupy produktów „${g.name}"` })}
                      >
                        <span className="min-w-0 truncate">{g.name}</span>
                        <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                          {g.items} tow.
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div>
              <p className="mb-1 text-xs font-medium text-muted-foreground">Pojedyncze produkty</p>
              {towary.length === 0 ? (
                <p className="text-xs text-muted-foreground">
                  {fraza === '' ? 'Wpisz frazę, żeby poszukać produktu' : 'Nic nie pasuje'}
                </p>
              ) : (
                <ul className="divide-y divide-border rounded-md border border-border">
                  {towary.slice(0, 40).map((i) => (
                    <li key={i.id}>
                      <button
                        type="button"
                        className={cn(
                          'flex w-full items-baseline justify-between gap-2 px-2.5 py-1.5 text-left text-sm hover:bg-accent',
                        )}
                        onClick={() => setWybor({ itemId: i.id, nazwa: `produktu „${i.name}"` })}
                      >
                        <span className="min-w-0 truncate">{i.name}</span>
                        <span className="shrink-0 font-mono text-xs text-muted-foreground">{i.itemNumber}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}

        {/*
          ⚠️⚠️ TO ZDANIE MUSI TU BYĆ. Człowiek szukający „podgrupy CUT" nie znajdzie jej na tej
          liście i bez wyjaśnienia uzna to za brak w portalu — a to ograniczenie SPS, obejmowane
          limitem na grupie nadrzędnej.
        */}
        <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
          <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
          Podgrupy nie ma na liście, bo SPS nie wyraża limitu na podgrupie — nadaj go na grupie nadrzędnej
          w Uprawnieniach, przy wpisie dostępu.
        </p>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Anuluj
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
